import {
  HOLDABLE_STATUSES,
  ORDER_LIFECYCLE_STATUSES,
  ORDER_STATUS,
  RETURN_RESUME_STATUSES,
  RETURNABLE_STATUSES,
  statusRank,
} from './orderConstants';

/*
 * One status vocabulary for every order screen.
 *
 * This replaces the two hand-maintained `STATUS_STYLES` maps that had already
 * drifted apart (OrderView used `rounded-md`, OrderPreviewDrawer `rounded-full`,
 * with different padding). Colour now encodes state and nothing else — the six
 * decorative hues (indigo, violet, blue…) are gone, and the three that remain
 * are the brand's own semantic tokens.
 *
 * Text colours are deliberately a shade darker than the raw --nysmah-* tokens:
 * #D4A017 on a 10%-tint background measures ~2.6:1, which fails WCAG AA. The
 * values below all clear 4.5:1 on their own backgrounds.
 */

export const TONE = {
  NEUTRAL: 'neutral',
  PENDING: 'pending',
  PROGRESS: 'progress',
  SUCCESS: 'success',
  DANGER: 'danger',
  HOLD: 'hold',
};

export const TONE_CLASS = {
  [TONE.NEUTRAL]: 'text-[#3B4A54] bg-[#EFF3F4] border-[#D6DFE2]',
  [TONE.PENDING]: 'text-[#7A5A07] bg-[#FBF3DF] border-[#E3CE95]',
  [TONE.PROGRESS]: 'text-[#33586A] bg-[#E9F1F4] border-[#BDD3DC]',
  [TONE.SUCCESS]: 'text-[#256B4C] bg-[#E7F4ED] border-[#AFD6C2]',
  [TONE.DANGER]: 'text-[#B23A32] bg-[#FBEDEC] border-[#E8B6B3]',
  [TONE.HOLD]: 'text-[#5B4B8A] bg-[#F1EFF8] border-[#CFC7E4]',
};

/** Solid colour for dots, stepper tracks and other non-text marks. */
export const TONE_MARK = {
  [TONE.NEUTRAL]: '#8A9BA8',
  [TONE.PENDING]: '#D4A017',
  [TONE.PROGRESS]: '#4B7A8E',
  [TONE.SUCCESS]: '#2E7D5B',
  [TONE.DANGER]: '#D14343',
  [TONE.HOLD]: '#6B5BA5',
};

const STATUS_TONE = {
  [ORDER_STATUS.PLACED]: TONE.PENDING,
  [ORDER_STATUS.CONFIRMED]: TONE.PROGRESS,
  [ORDER_STATUS.PROCESSING]: TONE.PROGRESS,
  [ORDER_STATUS.HANDED_TO_SHIPPING]: TONE.PROGRESS,
  [ORDER_STATUS.DELIVERED]: TONE.SUCCESS,
  [ORDER_STATUS.CANCELLED]: TONE.DANGER,
  // A return ends the order the way a cancellation does — money goes back,
  // goods go back on the shelf — so it shares the colour of that outcome.
  [ORDER_STATUS.RETURNED]: TONE.DANGER,
  // Held is deliberate and reversible, so it must not borrow the danger red
  // that cancellation uses. It reads as stopped, not broken.
  [ORDER_STATUS.ON_HOLD]: TONE.HOLD,
};

export const getStatusTone = (statusId) =>
  STATUS_TONE[statusId] || TONE.NEUTRAL;

/** True only for "On hold" — a pause, distinct from both stages and exits. */
export const isHoldStatus = (statusId) =>
  Number(statusId) === ORDER_STATUS.ON_HOLD;

/**
 * True only for "Returned" — the parcel came back. Like hold it sits beside
 * the path rather than ending the order: it is left again by sending the
 * parcel out or by cancelling.
 */
export const isReturnStatus = (statusId) =>
  Number(statusId) === ORDER_STATUS.RETURNED;

/*
 * A retired status (today: Rejected) is still a real value on old orders, so
 * it keeps its name in every list — but no order may be moved INTO it, so it
 * is never offered as a destination. The flag comes from `admin/statuses`.
 */
export const isRetiredOption = (option) => Boolean(option?.retired);

/** Status options in path order (STATUS_DISPLAY_ORDER), never API/id order. */
export const sortByPath = (options = []) =>
  options.slice().sort((a, b) => statusRank(a.value) - statusRank(b.value));

/*
 * An exit ends the order: cancelled, the retired rejected, anything else off
 * the track that is neither a pause nor a return.
 *
 * Hold and Returned are explicitly *not* exits even though they aren't stages
 * either. Left in the exit bucket they would inherit the "this order has
 * ended" lock on its contents — wrong for states whose entire purpose is to be
 * left again (resumed, or sent out again).
 */
export const isExitStatus = (statusId) =>
  !ORDER_LIFECYCLE_STATUSES.includes(Number(statusId)) &&
  !isHoldStatus(statusId) &&
  !isReturnStatus(statusId);

/** Off the track for any reason: an exit, a hold, or a return. */
export const isOffTrackStatus = (statusId) =>
  isExitStatus(statusId) || isHoldStatus(statusId) || isReturnStatus(statusId);

/*
 * Taking an order off the happy path is never a click we let someone make by
 * accident. Keying this off the exits rather than a single hardcoded id means
 * a second exit — anything added later — is guarded automatically instead of
 * silently slipping through as an ordinary status change. A return asks too:
 * it moves stock, even though it can be undone by sending the parcel again.
 */
export const isDestructiveStatus = (statusId) =>
  isExitStatus(statusId) || isReturnStatus(statusId);

/*
 * The progress track: a fixed sequence of stages, labelled by the API.
 *
 * Two earlier attempts got this wrong in opposite directions. The first
 * hardcoded the labels too ("Placed / Confirmed / Ready"), which disagreed with
 * what the backend calls those stages — status 102 is titled "Handed to
 * shipping company" — so the header pill and the track named the same state
 * differently. The
 * second took the whole list from the API and sorted it by id, which put
 * "Rejected" after "Completed" and made a finished order offer "Mark Rejected".
 *
 * The sequence is business logic and lives in ORDER_LIFECYCLE_STATUSES; only
 * the wording comes from the API. Statuses outside that list are exits.
 */
export const buildLifecycle = (statusOptions = []) => {
  const labels = new Map(
    statusOptions
      .filter((option) => option?.value != null)
      .map((option) => [Number(option.value), option.label]),
  );

  return ORDER_LIFECYCLE_STATUSES
    // A stage the backend no longer offers is dropped rather than rendered
    // with a placeholder name.
    .filter((id) => labels.has(id))
    .map((id) => ({ value: id, label: labels.get(id) }));
};

/**
 * Maps the order's current status onto the track.
 *
 * Returns one entry per stage with a `state` of 'done' | 'current' |
 * 'upcoming', plus a terminal descriptor when the order has left the happy
 * path — rejected, cancelled, or any other exit.
 */
export const resolveLifecycle = ({
  statusId,
  lifecycle = [],
  statusLabel,
  reachedIds,
}) => {
  const status = Number(statusId);
  const exited = isExitStatus(status);
  const held = isHoldStatus(status);
  // Held, returned and exited all sit off the track, so none claims a stage
  // — but they mean different things and are rendered differently.
  const offTrack = isOffTrackStatus(status);

  // The current status alone can't say how far an order got before it was
  // rejected or held, so the stages it actually reached are recovered from the
  // status-history timestamps instead. Without this the track would blank out
  // entirely and lose real information the timeline is already showing.
  const currentIndex = offTrack
    ? -1
    : lifecycle.findIndex((step) => Number(step.value) === status);

  const steps = lifecycle.map((step, index) => {
    let state = 'upcoming';
    if (currentIndex > index) state = 'done';
    else if (currentIndex === index) state = 'current';
    else if (offTrack && reachedIds?.has(step.value)) state = 'done';
    return { id: step.value, label: step.label, state };
  });

  return {
    steps,
    exited,
    held,
    offTrack,
    terminal: offTrack
      ? {
          label: statusLabel || (held ? 'On hold' : 'Ended'),
          tone: held
            ? TONE.HOLD
            : getStatusTone(status) === TONE.NEUTRAL
              ? TONE.DANGER
              : getStatusTone(status),
        }
      : null,
  };
};

/**
 * The next stage an operator would normally move the order to, so the primary
 * button can name it ("Mark Out for delivery") instead of making them open a
 * picker for the overwhelmingly common path.
 *
 * Null at the end of the track and on any exit status — a Completed or Rejected
 * order has no natural next step, and offering one is worse than offering none.
 */
export const getNextLifecycleStep = (statusId, lifecycle = []) => {
  const status = Number(statusId);
  // A held order must be resumed before it can advance; the backend refuses to
  // jump it straight to Handed to shipping company or Completed. A returned
  // one has no single natural next step: it is re-sent or cancelled.
  if (isOffTrackStatus(status)) return null;
  const index = lifecycle.findIndex((step) => Number(step.value) === status);
  if (index === -1 || index === lifecycle.length - 1) return null;
  const next = lifecycle[index + 1];
  return { id: next.value, label: next.label };
};

/*
 * Which statuses the backend will actually accept from here.
 *
 * The rules the server enforces:
 *
 *   · Into hold      — only from the pre-dispatch stages (New Order,
 *                      Under processing, Confirmed)
 *   · Out of hold    — only back to one of those (resume), or to Cancelled.
 *                      A held order cannot jump to Handed to shipping company
 *                      or Completed; it has to be resumed first.
 *   · Returned       — from any paid stage on the path (Under processing
 *                      to Completed), never from New Order or Hold. Out of
 *                      Returned: sent out again (Under processing, Confirmed,
 *                      Handed to shipping company, Completed) or Cancelled.
 *   · Retired        — never a destination (Rejected since 2026-10-21).
 *   · Dispatched, completed and exited orders cannot be held at all.
 *
 * Offering a transition the server will reject teaches people that the UI lies.
 * Everything that would 422 is filtered out before it reaches the menu, so the
 * only paths on screen are paths that work.
 */
export const allowedTransitions = ({
  fromStatus,
  statusOptions = [],
  canHold = false,
}) => {
  const from = Number(fromStatus);
  const held = isHoldStatus(from);
  const canEnterHold = canHold && HOLDABLE_STATUSES.includes(from);

  const allowed = statusOptions.filter((option) => {
    const to = Number(option.value);
    if (to === from) return false;
    if (isRetiredOption(option)) return false;

    if (isHoldStatus(to)) return canEnterHold;

    if (isReturnStatus(to)) return RETURNABLE_STATUSES.includes(from);

    if (held) {
      // Resume, or cancel. Nothing else.
      return HOLDABLE_STATUSES.includes(to) || isExitStatus(to);
    }

    if (isReturnStatus(from)) {
      // Send it out again, or cancel. Nothing else.
      return RETURN_RESUME_STATUSES.includes(to) || to === ORDER_STATUS.CANCELLED;
    }

    return true;
  });

  // Menus read top to bottom the way an order moves, whatever order the
  // API listed the statuses in.
  return sortByPath(allowed);
};

/**
 * Options for a per-item status picker.
 *
 * Two statuses are deliberately absent, for different reasons.
 *
 * **Hold** is order-level: the backend rejects it on the per-item endpoint for
 * everyone, super admins included.
 *
 * **Exits (Cancelled, Rejected)** are absent because reaching them through this
 * picker produced a broken half-state. `detail-status` only writes a status —
 * it recomputes nothing — so an item marked Cancelled here stayed in the
 * subtotal and the order's total never moved. Removing the item instead
 * (`DELETE .../items/<id>`) sets the very same Cancelled status *and*
 * recalculates the money, which is what anyone cancelling a line actually
 * means. Two routes to one visible state with different financial outcomes is
 * a trap, so the picker keeps the one that is arithmetically honest and the
 * remove button owns cancellation.
 *
 * Whatever the item currently *is* still has to appear, or the select has
 * nothing to match its value against and renders blank — so the current status
 * is included and disabled when it falls outside the selectable set.
 */
export const itemStatusOptions = (statusOptions = [], currentStatus) => {
  // Returned is order-level too: a parcel comes back whole, so the backend
  // refuses it line by line, exactly as it refuses hold.
  const selectable = sortByPath(
    statusOptions.filter(
      (option) =>
        !isHoldStatus(option.value) &&
        !isReturnStatus(option.value) &&
        !isExitStatus(option.value) &&
        !isRetiredOption(option),
    ),
  );

  const current = Number(currentStatus);
  const alreadyListed = selectable.some(
    (option) => Number(option.value) === current,
  );
  if (alreadyListed || currentStatus == null) return selectable;

  const currentOption = statusOptions.find(
    (option) => Number(option.value) === current,
  );
  return currentOption
    ? sortByPath([{ ...currentOption, disabled: true }, ...selectable])
    : selectable;
};

/*
 * Options for a status *picker*, as opposed to a list of transitions.
 *
 * `allowedTransitions` deliberately omits the current status — moving to where
 * you already are isn't a transition. A picker still has to contain it, because
 * a select resolves its trigger label by matching the current value against the
 * options it rendered: leave it out and the control displays blank, which is
 * exactly what happened to the orders list.
 *
 * So the current status is in the list, disabled, in its own place on the
 * path — not pinned to the top (Mia, 2026-10-21: the list should read as the
 * path, with the order's position simply marked). Everything else is
 * somewhere the order can actually go.
 */
export const statusPickerOptions = ({
  fromStatus,
  statusOptions = [],
  canHold = false,
}) => {
  const current = statusOptions.find(
    (option) => Number(option.value) === Number(fromStatus),
  );
  const transitions = allowedTransitions({
    fromStatus,
    statusOptions,
    canHold,
  });
  return current
    ? sortByPath([{ ...current, disabled: true }, ...transitions])
    : transitions;
};
